import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { getDirtyValues } from 'src/utils/getDirtyValues';
import { ReferenceRate } from 'src/features/referenceRates/utils/types';
import { referenceRateFormSchema, ReferenceRateFormSchemaValues } from 'src/features/referenceRates/utils/formSchema';
import { useGetReferenceRate } from 'src/features/referenceRates/api/useGetReferenceRate';
import ReferenceRateForm from 'src/features/referenceRates/components/forms/ReferenceRateForm';

interface ReferenceRateFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedReferenceRate?: ReferenceRate;
    children?: React.ReactNode;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: ReferenceRateFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function ReferenceRateFormModal<TData>({
    selectedReferenceRate,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: ReferenceRateFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { data: referenceRate } = useGetReferenceRate({
        id: selectedReferenceRate?.id ?? '',
        config: { enabled: !!selectedReferenceRate?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            name: referenceRate?.name ?? '',
            code: referenceRate?.code ?? '',
            currency: referenceRate?.currency ?? undefined,
            description: referenceRate?.description ?? undefined,
            is_active: referenceRate?.is_active ?? undefined,
        };
    }, [referenceRate]);

    const form = useForm<ReferenceRateFormSchemaValues>({
        resolver: zodResolver(referenceRateFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: ReferenceRateFormSchemaValues) => {
            // On edit only the touched fields go out, so an untouched currency is never resent.
            const payload = selectedReferenceRate
                ? (getDirtyValues(form.formState.dirtyFields, values) as ReferenceRateFormSchemaValues)
                : values;

            mutate(
                payload,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [selectedReferenceRate, form.formState.dirtyFields, form.setError, mutate, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ReferenceRateForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedReferenceRate && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ReferenceRateFormModal;
